import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import Button from "components/basic/button";
import Checkbox from "components/basic/checkbox";
import GooglePlacesSearch from "components/basic/googlePlacesSearch";
import Typography from "components/basic/typography";

interface MailingAddressSectionProps {
  businessAddress: string;
  sameAsBusiness: boolean;
  mailingAddress: string | null;
  onSave: (updates: {
    mailingSameAsBusiness: boolean;
    mailingAddress?: string | null;
  }) => Promise<boolean>;
  /** True while the box is unticked and no mailing address is saved yet. */
  onIncompleteChange: (incomplete: boolean) => void;
}

/** The mailing address, or the business address standing in for it. */
const MailingAddressSection: React.FC<MailingAddressSectionProps> = ({
  businessAddress,
  sameAsBusiness,
  mailingAddress,
  onSave,
  onIncompleteChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("profile");
  const [same, setSame] = useState(sameAsBusiness);
  const [draft, setDraft] = useState(mailingAddress ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSame(sameAsBusiness);
    setDraft(mailingAddress ?? "");
  }, [sameAsBusiness, mailingAddress]);

  useEffect(() => {
    onIncompleteChange(!same && sameAsBusiness);
  }, [same, sameAsBusiness, onIncompleteChange]);

  // Unticking only takes effect once a mailing address is saved below.
  const handleToggle = async (checked: boolean) => {
    setSame(checked);
    setError(null);
    if (checked && !sameAsBusiness) {
      setSame(await onSave({ mailingSameAsBusiness: true }));
    }
  };

  const handleSave = async () => {
    if (!draft.trim()) {
      setError(t("mailing.required"));
      return;
    }
    setSaving(true);
    await onSave({
      mailingSameAsBusiness: false,
      mailingAddress: draft.trim(),
    });
    setSaving(false);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(1),
      }}
    >
      <Typography variant="h5" style={{ fontWeight: "bold" }}>
        {t("mailing.title")}
      </Typography>

      {same || !sameAsBusiness ? (
        <Typography align="center">
          {same ? businessAddress : mailingAddress}
        </Typography>
      ) : null}

      <Checkbox
        isChecked={same}
        onChange={handleToggle}
        label={t("mailing.same")}
      />

      {!same && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1),
            width: "100%",
          }}
        >
          <GooglePlacesSearch
            value={draft}
            textAlign="center"
            placeHolder={t("mailing.search")}
            onInputChange={(text) => {
              setDraft(text);
              setError(null);
            }}
            onSelect={(place) => {
              setDraft(place.address);
              setError(null);
            }}
            error={error ?? undefined}
          />
          <Button
            variant="contained"
            color="warning"
            style={{ color: theme.palette.common.white, alignSelf: "center" }}
            onClick={handleSave}
            loading={saving}
            disabled={saving}
          >
            {t("mailing.save")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MailingAddressSection;
